'use client';

import { type SnapshotIn, t } from 'mobx-state-tree';

import { PropertyType } from '../propertyType';
import { type IObjectValue, type IProperty } from '../propertyTypes';

import { PropertyBase } from './propertyBase';
import { SelectProperty } from './selectProperty';

import type { IPropertySource, IPropertyStore } from '@/editor/property-editor/propertyStore';
import type { IAnyModelType, Instance } from 'mobx-state-tree';


export const ObjectPropertyBuilder = {
  create: (params: SnapshotIn<typeof ObjectProperty> & { value: IObjectValue, sources: IPropertySource[], store: IPropertyStore }) => {
    const property = ObjectProperty.create(params);
    property.setValue(params.value, params.sources);
    property.setStore(params.store);
    return property;
  }
};

/**
 * Model for object property that can contain other properties
 * NOTE: Use ObjectPropertyBuilder.create() to create an object property
 */
export const ObjectProperty = t.compose('ObjectProperty', PropertyBase,
  t.model({
    type: t.literal(PropertyType.Object),
    isHeader: t.optional(t.boolean, false),
    /** When true, renders as a non-collapsible class-section separator bar */
    isClassSection: t.optional(t.boolean, false),
    selectProperty: t.maybe(SelectProperty), // Select property for inline editing
    properties: t.map(t.late((): IAnyModelType => {
      const { AnyProperty } = require('../propertyTypes');
      return AnyProperty;
    })),
    defaultExpanded: t.optional(t.boolean, false),
    isEditInline: t.optional(t.boolean, false),
  })
).volatile(() => ({
  value: null as IObjectValue,
  sources: undefined as IPropertySource[] | undefined,
  onReset: undefined as (() => void) | undefined,
  onExpansionChange: undefined as ((expanded: boolean) => void) | undefined,
})).views(self => ({
  get orderedProperties() {
    const properties = Array.from(self.properties.values())
      .filter(p => !p.hidden)
      .sort((a, b) => a.order - b.order);
    return properties;
  },
})).actions(self => ({
  setValue(value: IObjectValue, sources: IPropertySource[], fromPanel = true, initial = false) {
    const changed = self.value !== value || self.sources !== sources;
    if (!changed) return;
    self.value = value;
    self.sources = sources;
    self.notifyValueChange(self.path, value, { fromPanel, initial, property: self as Instance<typeof ObjectProperty> });
  },
  setSources(sources: IPropertySource[]) {
    self.sources = sources;
  },
  setIsHeader(isHeader: boolean) {
    self.isHeader = isHeader;
  },
  setIsEditInline(isEditInline: boolean) {
    self.isEditInline = isEditInline;
  },
  setSelectProperty(selectProperty: Instance<typeof SelectProperty> | undefined) {
    self.selectProperty = selectProperty;
  },
  addProperty(property: IProperty) {
    self.properties.put(property);
    self.store?.clearPropertyCache();
  },
  addProperties(properties: IProperty[]) {
    properties.forEach(property => this.addProperty(property));
  },
  removeProperty(key: string) {
    self.properties.delete(key);
    self.store?.clearPropertyCache();
  },
  clearProperties() {
    self.properties.clear();
    self.store?.clearPropertyCache();
  },
  setOnReset(callback: (() => void) | undefined) {
    self.onReset = callback;
  },
  setOnExpansionChange(callback: ((expanded: boolean) => void) | undefined) {
    self.onExpansionChange = callback;
  },
}));

